import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { SawProductRun } from "@/pages/sawlines/models/types";
import type { WorkOrderMatch } from "@/pages/sawlines/repositories/sawWriteRepository";

export interface SawEntryQueueEntry {
	EntryBundleId: number;
	TagNo: string;
	SectionName: string | null;
	GradeName: string | null;
	HeatNo: string | null;
	MillLengthIn: number | null;
	Pieces: number | null;
	ScannedPartNumber: string | null;
	ScannedAt: string;
	ScannedByEmployee: string | null;
	WorkOrderMatch: WorkOrderMatch;
	MatchesRunning: boolean;
	MatchesNext: boolean;
}

export interface SawEntryWorkOrder
	extends Pick<
		SawProductRun,
		| "SectionName"
		| "GradeName"
		| "MillLength"
		| "TargetLength"
		| "MillPcB"
		| "FinishPcB"
	> {
	Position: "Current" | "Next";
	WorkOrderId: number;
	WorkOrderNumber: string;
	PartNumber: string;
	Quantity: number;
	PiecesCut: number;
	MillBundlesLoaded: number;
}

export interface SawEntryLoaded
	extends Pick<
		SawEntryQueueEntry,
		| "EntryBundleId"
		| "TagNo"
		| "SectionName"
		| "GradeName"
		| "MillLengthIn"
		| "ScannedPartNumber"
	> {
	LoadedAt: string;
	LoadedByEmployee: string | null;
	// snapshot at Loaded: WO it went into, match the operator saw
	LoadedWorkOrderNumber: string | null;
	LoadedMatch: WorkOrderMatch;
	CompletedWorkOrderNumber: string | null;
	StartedWorkOrderNumber: string | null;
}

export interface SawEntryQueueData {
	entries: SawEntryQueueEntry[];
	loaded: SawEntryLoaded[];
	limit: number;
	running?: SawEntryWorkOrder;
	next?: SawEntryWorkOrder;
}

const call = async (procedure: string, parameters: spParameter[]) => {
	const response = await new MESApiService().callV2(procedure, parameters);
	if (!response.ok)
		throw new Error(response.message || "Unable to update the material queue.");
	return response;
};

export const getSawEntryQueue = async (
	assetId: number,
): Promise<SawEntryQueueData> => {
	const response = await call("[FAB].[GetSawEntryQueue]", [
		{ name: "AssetId", value: assetId },
	]);
	const workOrders = get(
		response,
		"data.tables[2].rows",
		[],
	) as SawEntryWorkOrder[];
	return {
		entries: get(response, "data.tables[1].rows", []) as SawEntryQueueEntry[],
		limit: get(response, "data.tables[0].rows[0].Limit", 5) as number,
		loaded: get(response, "data.tables[3].rows", []) as SawEntryLoaded[],
		running: workOrders.find((wo) => wo.Position === "Current"),
		next: workOrders.find((wo) => wo.Position === "Next"),
	};
};

export const reorderSawEntryQueue = (
	assetId: number,
	entryBundleId: number,
	direction: -1 | 1,
	employee: string,
) =>
	call("[FAB].[ReorderSawEntryQueue]", [
		{ name: "AssetId", value: assetId },
		{ name: "EntryBundleId", value: entryBundleId },
		{ name: "Direction", value: direction },
		{ name: "EmployeeNumber", value: employee },
	]);

export interface WorkOrderSwitch {
	completeWorkOrderId?: number;
	startWorkOrderId?: number;
}

export const removeSawEntryScan = (
	assetId: number,
	entryBundleId: number,
	employee: string,
) =>
	call("[FAB].[RemoveSawEntryScan]", [
		{ name: "AssetId", value: assetId },
		{ name: "EntryBundleId", value: entryBundleId },
		{ name: "EmployeeNumber", value: employee },
	]);

export const loadSawEntryScan = (
	assetId: number,
	entryBundleId: number,
	employee: string,
	{ completeWorkOrderId, startWorkOrderId }: WorkOrderSwitch = {},
) =>
	call("[FAB].[LoadSawEntryScan]", [
		{ name: "AssetId", value: assetId },
		{ name: "EntryBundleId", value: entryBundleId },
		{ name: "EmployeeNumber", value: employee },
		{ name: "CompleteWorkOrderId", value: completeWorkOrderId ?? null },
		{ name: "StartWorkOrderId", value: startWorkOrderId ?? null },
	]);

export const restoreSawEntryScan = (
	assetId: number,
	entryBundleId: number,
	employee: string,
) =>
	call("[FAB].[RestoreSawEntryScan]", [
		{ name: "AssetId", value: assetId },
		{ name: "EntryBundleId", value: entryBundleId },
		{ name: "EmployeeNumber", value: employee },
	]);
